/**
 * SidePanelController
 *
 * A reusable Stimulus controller that manages animated sliding panel UI components throughout
 * the Metarina marina management system. Provides consistent panel behavior with smooth
 * animations, automatic Turbo integration for form submissions, and contextual auto-opening
 * capabilities. Used extensively across billing, customer management, and other features to
 * display forms and detailed content without full page reloads, maintaining user workflow
 * and context while providing a polished, responsive interface experience.
 * Mounted in app/views/layouts/_marina_login.html.erb:110
 *
 * @see {@link https://www.notion.so/metarina/Side-Panel-Controller-Documentation-23a0bdb0debb8058b47cd41024a75712} Complete Documentation
 */

import { Controller } from "stimulus";
import { enter, leave } from "el-transition";

export default class extends Controller {
  static targets = ["container", "backdrop", "panel"];

  static values = { shouldOpen: Boolean };

  connect() {
    // Auto-close on successful form submissions
    document.addEventListener("turbo:submit-end", async (event) => {
      if (event.detail.success) {
        await this.close();
      }
    });

    // Close on Escape key press
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener("keydown", this.handleEscapeKey);

    // Auto-open if shouldOpen is true - but without animation on initial load
    if (this.shouldOpenValue) {
      this.openWithoutAnimation();
    }
  }

  disconnect() {
    // Clean up event listener when controller is disconnected
    document.removeEventListener("keydown", this.handleEscapeKey);
  }

  handleEscapeKey(event) {
    // Close panel if Escape key is pressed and panel is visible
    if (event.key === "Escape" && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  }

  open() {
    this.containerTarget.classList.remove("hidden");
    enter(this.backdropTarget);
    enter(this.panelTarget);
  }

  openWithoutAnimation() {
    // Open immediately without transitions for page load
    this.containerTarget.classList.remove("hidden");
    this.backdropTarget.classList.remove("opacity-0");
    this.panelTarget.classList.remove("translate-x-full");
  }

  async close() {
    return Promise.all([
      leave(this.backdropTarget),
      leave(this.panelTarget),
    ]).then(() => {
      this.containerTarget.classList.add("hidden");
    });
  }

  // Helper method to check if panel is currently open
  isOpen() {
    return !this.containerTarget.classList.contains("hidden");
  }
}
